/**
 * @fileoverview Organization Switcher component for superusers.
 * Allows superusers to switch between organizations or view all organizations.
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { setSelectedOrganization } from '../store/slices/authSlice';
import api from '../services/api';

/**
 * Organization switcher dropdown for superusers.
 * Shows a dropdown to switch between organizations or view all.
 * Only renders for superusers.
 */
const OrganizationSwitcher = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const selectedOrganization = useSelector((state) => state.auth.selectedOrganization);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await api.get('/v1/organizations/');
        setOrganizations(response.data);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.is_superuser) {
      fetchOrganizations();
    }
  }, [user]);

  // Only show for superusers
  if (!user?.is_superuser) {
    return null;
  }

  const handleChange = (event) => {
    const orgId = event.target.value;
    if (orgId === 'all') {
      dispatch(setSelectedOrganization(null));
    } else {
      const org = organizations.find(o => o.id === orgId);
      dispatch(setSelectedOrganization(org));
    }
    // Reload page to refetch data with new org context
    window.location.reload();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <CircularProgress size={20} color="inherit" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
      <Business sx={{ mr: 1, color: 'inherit', opacity: 0.8 }} />
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select
          value={selectedOrganization?.id || 'all'}
          onChange={handleChange}
          displayEmpty
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: 'inherit',
            '& .MuiSelect-select': {
              py: 0.5,
              color: 'inherit',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiSvgIcon-root': {
              color: 'inherit',
            },
          }}
        >
          <MenuItem value="all">
            <em>All Organizations</em>
          </MenuItem>
          {organizations.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name} ({org.code})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default OrganizationSwitcher;
